import React, { useEffect } from 'react';
import styled from 'styled-components';

const CellWrapper = styled.td`
    text-align: center;
    vertical-align: middle;
    padding: 4px 2px;
    height: 40px;
    min-height: 40px;
    position: relative;
    cursor: ${props => props.canEdit ? 'pointer' : 'default'};
    background-color: ${props => {
        if (props.isToday) return '#e8f4f8';
        if (props.isWeekend) return '#fafafa';
        return 'transparent';
    }};
    border: 1px solid #ddd;
    user-select: none;
    
    &:hover {
        background-color: ${props => props.canEdit ? '#f0f0f0' : 'inherit'};
    }
    
    &.editable {
        cursor: pointer;
    }
    
    &.editable:hover {
        background-color: #f0f0f0;
        border-color: #2196f3;
    }
    
    &.weekend {
        background-color: #fafafa;
    }
    
    &.today {
        background-color: #e8f4f8;
    }
`;

const ScheduleItemsContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3px;
    height: 100%;
    flex-wrap: wrap;
`;

const ScheduleDot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    cursor: ${props => props.canEdit ? 'pointer' : 'default'};
    transition: all 0.2s ease;
    flex-shrink: 0;
    background-color: ${props => props.color || '#f0f0f0'};
    
    &:hover {
        transform: ${props => props.canEdit ? 'scale(1.2)' : 'scale(1.1)'};
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        border-color: rgba(0, 0, 0, 0.3);
    }
`;

const ScheduleCoordinationCell = ({
    date,
    schedules,
    canEdit,
    getColorForType,
    scheduleTypes,
    onDateDoubleClick,
    onRemoveSchedule,
    isWeekend,
    isToday,
    userUuid,
    formatDate
}) => {
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        console.log('Cell double clicked', { canEdit, userUuid, date: formatDate(date) });
        
        if (canEdit && onDateDoubleClick) {
            onDateDoubleClick(date, userUuid);
        } else {
            console.log('無法編輯：', { 
                canEdit, 
                userUuid, 
                currentUser: window.currentUser,
                reason: !canEdit ? 'canEdit is false' : 'onDateDoubleClick is null'
            });
        }
    };

    // 根據日期類型決定顏色
    const getDotColor = (schedule) => {
        const dayType = schedule.day_type || 'normal';
        if (dayType === 'rest_day') {
            return '#ff9800'; // 休息日：橙色
        } else if (dayType === 'regular_holiday') {
            return '#f44336'; // 例假日：紅色
        }
        return getColorForType(schedule.type_id);
    };

    // 取得 tooltip 文字
    const getTooltipText = (schedule) => {
        const scheduleType = schedule.type_name || '未知';
        const dayType = schedule.day_type || 'normal';
        const timeRange = schedule.time_range || '';
        
        let dayTypeLabel = '';
        if (dayType === 'rest_day') {
            dayTypeLabel = ' [休息日]';
        } else if (dayType === 'regular_holiday') {
            dayTypeLabel = ' [例假日]';
        }
        if (schedule.is_national_holiday) {
            dayTypeLabel += ' [國定假日]';
        }
        
        return scheduleType + dayTypeLabel + (timeRange ? ' (' + timeRange + ')' : '');
    };

    // 調試信息
    useEffect(() => {
        if (canEdit) {
            console.log('Cell is editable', { userUuid, date: formatDate(date) });
        }
    }, [canEdit, userUuid, date, formatDate]);

    return (
        <CellWrapper
            className={`schedule-cell ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''} ${canEdit ? 'editable' : ''}`}
            canEdit={canEdit}
            isWeekend={isWeekend}
            isToday={isToday}
            onDoubleClick={handleDoubleClick}
            role="gridcell"
            title={canEdit ? '雙擊新增排班' : '僅供查看'}
        >
            {schedules.length > 0 && (
                <ScheduleItemsContainer>
                    {schedules.map((schedule, index) => (
                        <ScheduleDot
                            key={schedule.id || index}
                            color={getDotColor(schedule)}
                            canEdit={canEdit}
                            onClick={(e) => {
                                if (canEdit && onRemoveSchedule) {
                                    e.stopPropagation();
                                    onRemoveSchedule(schedule.id);
                                }
                            }}
                            title={getTooltipText(schedule)}
                        />
                    ))}
                </ScheduleItemsContainer>
            )}
        </CellWrapper>
    );
};

export default ScheduleCoordinationCell;
